import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { NAV, LINKS, ASSETS } from "@/data/content";
import { Dot } from "@/components/System";

const ActionButton = ({ href, children, disabled, testid, primary }) => {
    const base = `border px-3 py-1.5 font-mono text-[10px] tracking-[0.22em] transition-colors duration-200 ${
        primary
            ? "border-[#1688FF]/60 bg-[#006CFF]/20 text-[#F5F9FF] hover:bg-[#006CFF]/40"
            : "border-[rgba(0,217,255,0.25)] text-[#B8C7D9] hover:border-[#00D9FF]/60 hover:text-[#00D9FF]"
    }`;
    if (disabled) {
        return (
            <span data-testid={testid} title="PAIR DATA NOT AVAILABLE" className={`${base} cursor-not-allowed opacity-40`}>
                {children}
            </span>
        );
    }
    return (
        <a data-testid={testid} href={href} target="_blank" rel="noopener noreferrer" className={base}>
            {children}
        </a>
    );
};

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    return (
        <>
            <header className="fixed inset-x-0 top-0 z-50 border-b border-[rgba(0,217,255,0.12)] bg-[#05070B]/85 backdrop-blur-md">
                <div className="flex items-center justify-between gap-4 px-4 py-2.5 sm:px-6">
                    <div className="flex items-center gap-3">
                        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5" aria-label="AgentVM home">
                            <img src={ASSETS.logo} alt="AgentVM logo" className="h-7 w-7 border border-[rgba(0,217,255,0.3)] object-cover" />
                            <span className="font-mono text-sm font-semibold tracking-[0.14em] text-[#F5F9FF]">
                                Agent<span className="text-[#00D9FF]">VM</span>
                            </span>
                        </Link>
                        <span className="vm-micro-dim hidden items-center gap-2 border-l border-[rgba(0,217,255,0.15)] pl-3 lg:flex">
                            <Dot /> AGENTVM SYSTEM ONLINE
                        </span>
                    </div>

                    <nav className="hidden items-center gap-1 xl:flex" aria-label="Primary">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                data-testid={`nav-${item.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] transition-colors duration-200 ${
                                        isActive
                                            ? "bg-[#00D9FF]/10 text-[#00D9FF]"
                                            : "text-[#B8C7D9]/80 hover:text-[#F5F9FF]"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <div className="hidden items-center gap-2 sm:flex">
                            <ActionButton href={LINKS.buy} testid="nav-buy" primary>BUY</ActionButton>
                            <ActionButton disabled={!LINKS.chart} href={LINKS.chart} testid="nav-chart">CHART</ActionButton>
                            <ActionButton href={LINKS.x} testid="nav-x">X</ActionButton>
                        </div>
                        <button
                            data-testid="nav-menu-toggle"
                            onClick={() => setOpen(!open)}
                            aria-expanded={open}
                            aria-label="Toggle menu"
                            className="flex h-8 w-8 flex-col items-center justify-center gap-1.5 border border-[rgba(0,217,255,0.25)] xl:hidden"
                        >
                            <span className={`h-px w-4 bg-[#00D9FF] transition-transform ${open ? "translate-y-[3.5px] rotate-45" : ""}`} />
                            <span className={`h-px w-4 bg-[#00D9FF] transition-transform ${open ? "-translate-y-[3px] -rotate-45" : ""}`} />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-40 bg-[#05070B]/97 backdrop-blur-xl xl:hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="flex h-full flex-col justify-center px-8 pt-16">
                            <div className="vm-micro mb-6">AGENTVM://MENU</div>
                            <nav className="flex flex-col gap-1" aria-label="Mobile">
                                {[...NAV, { label: "TERMINAL", path: "/terminal" }, { label: "SOURCES", path: "/sources" }, { label: "TOKEN", path: "/token" }, { label: "LAUNCH", path: "/launch" }].map((item, i) => (
                                    <motion.div
                                        key={item.path}
                                        initial={{ opacity: 0, x: -16 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.05 * i }}
                                    >
                                        <NavLink
                                            to={item.path}
                                            onClick={() => setOpen(false)}
                                            data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                                            className={({ isActive }) =>
                                                `flex items-center gap-4 border-b border-[rgba(0,217,255,0.08)] py-3 font-mono text-lg tracking-[0.2em] ${
                                                    isActive || location.pathname === item.path ? "text-[#00D9FF]" : "text-[#F5F9FF]/85"
                                                }`
                                            }
                                        >
                                            <span className="text-[10px] text-[#B8C7D9]/40">{String(i + 1).padStart(2, "0")}</span>
                                            {item.label}
                                        </NavLink>
                                    </motion.div>
                                ))}
                            </nav>
                            <div className="mt-8 flex gap-2">
                                <ActionButton href={LINKS.buy} testid="mobile-nav-buy" primary>BUY</ActionButton>
                                <ActionButton disabled={!LINKS.chart} href={LINKS.chart} testid="mobile-nav-chart">CHART</ActionButton>
                                <ActionButton href={LINKS.x} testid="mobile-nav-x">X</ActionButton>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
